import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaCheck, 
  FaTimes, 
  FaEnvelope, 
  FaPhone, 
  FaUser,
  FaClock,
  FaSpinner,
  FaExchangeAlt
} from 'react-icons/fa';
import useSWR from 'swr';
import Fetcher from '../../lib/Fetcher';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';

function SpecializationRequests() {
  const navigate = useNavigate();
  const { data, error, mutate } = useSWR('/api/admin/specialization-requests', Fetcher);
  const [loading, setLoading] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(null);

  const requests = data?.data || [];

  const handleApprove = async (trainerId) => {
    setLoading(trainerId);
    try {
      await HttpInterceptor.post(`/api/admin/specialization-requests/${trainerId}/approve`);
      toast.success('Specialization change approved successfully');
      mutate();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve specialization change');
    } finally {
      setLoading(null);
    }
  };

  const handleReject = async (trainerId) => {
    setLoading(trainerId);
    try {
      await HttpInterceptor.post(`/api/admin/specialization-requests/${trainerId}/reject`);
      toast.success('Specialization change request rejected');
      setShowRejectModal(null);
      mutate();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject specialization change');
    } finally {
      setLoading(null);
    }
  };

  if (error) {
    return (
      <div className="p-4 md:p-8 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load specialization requests</p>
          <button
            onClick={() => mutate()}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Specialization Change Requests</h1>
          <p className="text-gray-600">Review and approve business partner specialization change requests</p>
        </div>

        {requests.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <FaCheck className="text-6xl text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">All Caught Up!</h2>
            <p className="text-gray-600">There are no pending specialization change requests at the moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {requests.map((trainer) => (
              <div key={trainer._id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                      <FaUser className="text-orange-600 text-xl" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 capitalize">{trainer.name}</h3>
                      <p className="text-sm text-gray-600">Current: {trainer.service}</p>
                    </div>
                  </div>
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                    <FaClock className="inline mr-1" />
                    Pending
                  </span>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="bg-blue-50 rounded-lg p-3 border-l-4 border-blue-500">
                    <div className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      <FaExchangeAlt className="mr-2 text-blue-500" />
                      Requested Change
                    </div>
                    <div className="text-lg font-bold text-blue-700">
                      {trainer.serviceChangeRequest}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <FaEnvelope className="mr-2 text-gray-400" />
                      <span className="truncate">{trainer.email}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <FaPhone className="mr-2 text-gray-400" />
                      <span>{trainer.mobile}</span>
                    </div>
                    {trainer.yearsOfExperience !== undefined && (
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Experience:</span> {trainer.yearsOfExperience} years
                      </div>
                    )}
                    {trainer.certification && (
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Certification:</span> {trainer.certification}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => handleApprove(trainer._id)}
                    disabled={loading === trainer._id}
                    className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {loading === trainer._id ? (
                      <FaSpinner className="animate-spin" />
                    ) : (
                      <>
                        <FaCheck className="mr-2" />
                        Approve
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowRejectModal(trainer._id)}
                    disabled={loading === trainer._id}
                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    <FaTimes className="mr-2" />
                    Reject
                  </button>
                </div>

                {/* Reject Modal */}
                {showRejectModal === trainer._id && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                      <h3 className="text-xl font-bold text-gray-900 mb-4">Reject Specialization Change</h3>
                      <p className="text-gray-600 mb-4">
                        Are you sure you want to reject {trainer.name}'s specialization change request from <strong>{trainer.service}</strong> to <strong>{trainer.serviceChangeRequest}</strong>?
                      </p>
                      <div className="flex space-x-3">
                        <button
                          onClick={() => setShowRejectModal(null)}
                          className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleReject(trainer._id)}
                          disabled={loading === trainer._id}
                          className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading === trainer._id ? (
                            <FaSpinner className="animate-spin mx-auto" />
                          ) : (
                            'Reject'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SpecializationRequests;

